import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  ArrowUpRight, Check, Loader2, Lock, ShieldCheck, ShoppingBag, Sparkles,
} from "lucide-react";

import { API, SHOPIFY_APP_STORE_URL } from "./constants";
import SectionLabel from "./SectionLabel";
import { CASH_IMPACT_LABEL, OPPS_COUNT } from "./aigoMetrics";
import { useUpsellTrigger } from "./UpsellModal";

/* Feature matrix — Free = diagnosis, Pro = execution */
const FEATURE_MATRIX = [
  { label: "Connect Shopify + Google/Meta/Klaviyo",   free: true,  pro: true },
  { label: "Growth Score · 6 pillars",                 free: true,  pro: true },
  { label: `See every $-Leak · $/mo impact`,           free: true,  pro: true },
  { label: `Untapped-opportunity capture · ${OPPS_COUNT} opps`, free: true, pro: true },
  { label: "Contribution-Margin P&L · Profit Waterfall", free: true, pro: true },
  { label: "1-click Implement",                        free: false, pro: true },
  { label: "Approve-all-safe · +$76.8k in one tap",    free: false, pro: true },
  { label: "Adjust-Impact sliders",                    free: false, pro: true },
  { label: "Autonomous Mode",                          free: false, pro: true },
  { label: "Operator Modules · Image / SEO / Inventory", free: false, pro: true },
  { label: "AI Profit Copilot chat",                   free: false, pro: true },
  { label: "Klaviyo lifecycle automations",            free: false, pro: true },
];

/* Public briefing subscribe modal (unchanged from previous session) */
const BriefingSubscribeModal = ({ onClose }) => {
  const [email, setEmail] = useState("");
  const [shop, setShop] = useState("");
  const [tz] = useState(() => {
    try { return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC"; } catch { return "UTC"; }
  });
  const [state, setState] = useState({ loading: false, ok: null, msg: "" });

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setState({ loading: true, ok: null, msg: "" });
    try {
      const { data } = await axios.post(`${API}/briefing/public-subscribe`, {
        email, shop: shop || undefined, send_hour: 7, timezone: tz,
      });
      setState({ loading: false, ok: true, msg: `You’re in — first briefing lands tomorrow at 7 AM ${data.timezone}.` });
      if (window.gtag) window.gtag("event", "briefing_subscribe", { source: "pricing_card", timezone: tz });
    } catch (err) {
      const detail = err.response?.data?.detail;
      setState({ loading: false, ok: false, msg: typeof detail === "string" ? detail : "Couldn’t subscribe — try again." });
    }
  };

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      data-testid="briefing-modal-backdrop"
      className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm"
      style={{ animation: "fadein 200ms ease-out both" }}
      onClick={onClose}
    >
      <div
        data-testid="briefing-modal"
        className="w-full max-w-md rounded-2xl bg-white border border-slate-200 shadow-2xl shadow-emerald-500/10 overflow-hidden"
        style={{ animation: "lift 280ms cubic-bezier(.2,.8,.2,1) both" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-5 sm:p-6">
          <div className="flex items-start justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-emerald-700">7 AM Profit Briefing</div>
              <h3 className="mt-1 text-lg sm:text-xl font-bold text-slate-900 leading-tight">Get tomorrow’s briefing — on us.</h3>
            </div>
            <button data-testid="briefing-modal-close" onClick={onClose} aria-label="Close"
              className="shrink-0 w-8 h-8 rounded-full hover:bg-slate-100 text-slate-500 text-lg leading-none">×</button>
          </div>
          <p className="mt-2 text-sm text-slate-600 leading-relaxed">
            One short email at <b>7 AM {tz}</b> with yesterday’s revenue, ad spend, ROAS, and today’s top 1-click move waiting in your operator.
          </p>
          {state.ok ? (
            <div data-testid="briefing-modal-success" className="mt-5 p-4 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm font-semibold">
              <Check className="w-4 h-4 inline mr-1" /> {state.msg}
            </div>
          ) : (
            <form data-testid="briefing-modal-form" onSubmit={submit} className="mt-4 space-y-3">
              <input data-testid="briefing-modal-email" type="email" required placeholder="ops@yourbrand.com"
                value={email} onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 rounded-xl border border-slate-300 text-sm focus:outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100" />
              <input data-testid="briefing-modal-shop" type="text" placeholder="acme.myshopify.com (optional)"
                value={shop} onChange={(e) => setShop(e.target.value)}
                className="w-full px-4 py-3 rounded-xl border border-slate-300 text-sm focus:outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100" />
              <button data-testid="briefing-modal-submit" type="submit" disabled={state.loading}
                className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-emerald-500 hover:bg-emerald-600 disabled:opacity-60 text-white text-sm font-semibold transition shadow-sm shadow-emerald-500/30">
                {state.loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Subscribe — 7 AM {tz} <ArrowUpRight className="w-4 h-4" /></>}
              </button>
              {state.msg && state.ok === false && (
                <div data-testid="briefing-modal-error" className="text-xs text-rose-600 font-semibold">{state.msg}</div>
              )}
              <p className="text-[10px] text-slate-400 text-center leading-relaxed pt-1">No spam. One-tap unsubscribe.</p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

/* === Price cards === */
const FreeCard = () => (
  <div
    data-testid="plan-free"
    className="relative rounded-3xl p-7 border border-slate-200 bg-white transition hover:-translate-y-0.5 hover:shadow-md flex flex-col"
  >
    <div className="text-slate-500 text-[10px] uppercase tracking-[0.18em] font-bold">Free · diagnosis</div>
    <div className="mt-3 flex items-baseline gap-1">
      <span className="text-slate-900 text-5xl tracking-tight font-extrabold">$0</span>
      <span className="text-slate-500 text-sm">forever</span>
    </div>
    <p className="text-slate-600 text-sm mt-2">See every leak. See exactly how much money is on the table.</p>
    <ul className="mt-5 space-y-2.5 flex-1">
      <li className="flex items-start gap-2 text-sm text-slate-700"><Check className="w-4 h-4 mt-0.5 text-emerald-600 shrink-0" /> Connect Shopify + Google + Meta + Klaviyo</li>
      <li className="flex items-start gap-2 text-sm text-slate-700"><Check className="w-4 h-4 mt-0.5 text-emerald-600 shrink-0" /> Growth Score · 6 pillars</li>
      <li className="flex items-start gap-2 text-sm text-slate-700"><Check className="w-4 h-4 mt-0.5 text-emerald-600 shrink-0" /> Every $-Leak &amp; Untapped opportunity</li>
      <li className="flex items-start gap-2 text-sm text-slate-700"><Check className="w-4 h-4 mt-0.5 text-emerald-600 shrink-0" /> Exact $/mo impact + confidence tags</li>
      <li className="flex items-start gap-2 text-sm text-slate-700"><Check className="w-4 h-4 mt-0.5 text-emerald-600 shrink-0" /> Contribution-Margin P&amp;L + Waterfall</li>
    </ul>
    <a
      href={SHOPIFY_APP_STORE_URL}
      target="_blank" rel="noopener noreferrer"
      data-testid="plan-free-cta"
      className="mt-6 w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold transition"
    >
      <ShoppingBag className="w-4 h-4" /> Start free · no card <ArrowUpRight className="w-4 h-4" />
    </a>
  </div>
);

const ProCard = ({ id, tier, price, cadence, tagline, features, cta, plan, ribbon, badge }) => {
  const [showBriefing, setShowBriefing] = useState(false);

  return (
    <div
      data-testid={id}
      className={`relative rounded-3xl p-7 border bg-white transition flex flex-col ${
        ribbon ? "border-emerald-300 shadow-xl shadow-emerald-500/20 ring-1 ring-emerald-100 md:-translate-y-1"
               : "border-slate-200 hover:border-emerald-300 hover:shadow-md hover:-translate-y-0.5"
      }`}
    >
      {ribbon && (
        <div className="absolute -top-3 left-6 px-3 py-1 rounded-full bg-emerald-500 text-white text-[10px] uppercase tracking-[0.15em] font-bold shadow-sm shadow-emerald-500/30">
          {ribbon}
        </div>
      )}
      <div className="text-emerald-700 text-[10px] uppercase tracking-[0.18em] font-bold">{tier}</div>
      <div className="mt-3 flex items-baseline gap-1">
        <span className="text-slate-900 text-5xl tracking-tight font-extrabold">{price}</span>
        <span className="text-slate-500 text-sm">{cadence}</span>
      </div>
      <p className="text-slate-600 text-sm mt-2">{tagline}</p>
      {badge && (
        <div className="mt-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 text-[10px] font-bold border border-emerald-200 self-start uppercase tracking-[0.14em]">
          <Sparkles className="w-3 h-3" /> {badge}
        </div>
      )}
      <ul className="mt-5 space-y-2.5 flex-1">
        {features.map((f) => (
          <li key={f} className="flex items-start gap-2 text-sm text-slate-700">
            <Check className="w-4 h-4 mt-0.5 text-emerald-600 shrink-0" /> {f}
          </li>
        ))}
      </ul>
      <a
        href={SHOPIFY_APP_STORE_URL}
        target="_blank" rel="noopener noreferrer"
        data-testid={`${id}-cta`}
        onClick={() => {
          try {
            if (window.gtag) window.gtag("event", "pricing_cta_click", { plan, tier });
          } catch (_) { /* analytics soft-fail */ }
        }}
        className={`mt-6 w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full text-sm font-semibold transition ${
          ribbon ? "bg-emerald-500 hover:bg-emerald-600 text-white shadow-sm shadow-emerald-500/30"
                 : "border border-slate-300 text-slate-900 hover:border-emerald-400 hover:text-emerald-700 bg-white"
        }`}
      >
        {cta} <ArrowUpRight className="w-4 h-4" />
      </a>
      <div className="mt-2 text-center text-[10px] text-slate-400 leading-relaxed">
        Install free · unlock Pro in-app when you approve your first fix.
      </div>
      {plan === "operator" && (
        <button
          data-testid={`${id}-briefing-cta`}
          onClick={() => setShowBriefing(true)}
          className="mt-3 w-full text-center text-xs font-semibold text-emerald-700 hover:text-emerald-800 underline underline-offset-4 decoration-emerald-300 hover:decoration-emerald-500 transition"
        >
          Or — get the 7 AM Profit Briefing free →
        </button>
      )}
      {showBriefing && <BriefingSubscribeModal onClose={() => setShowBriefing(false)} />}
    </div>
  );
};

/* === Pricing section === */
export const Pricing = () => {
  const { modal, open } = useUpsellTrigger();
  return (
  <section
    id="pricing"
    data-testid="pricing-section"
    className="px-6 md:px-10 py-20 md:py-24 border-t border-slate-100"
  >
    <div className="max-w-7xl mx-auto">
      <div className="text-center mb-12">
        <SectionLabel>Pricing · diagnosis free · execution paid</SectionLabel>
        <h2 className="text-3xl md:text-5xl lg:text-6xl text-slate-900 max-w-4xl mx-auto mt-4 mb-4 tracking-tight font-bold leading-[1.05]">
          <span className="text-emerald-600">Explore</span> every opportunity free. <br className="hidden md:block" />
          <span className="text-slate-900">Put fixes to work</span> on Pro.
        </h2>
        <p className="text-slate-600 max-w-2xl mx-auto text-base md:text-lg leading-relaxed">
          Pro pays for itself the first time you approve one fix. That’s the deal.
        </p>
      </div>

      <div className="grid md:grid-cols-3 gap-4 md:gap-5 max-w-5xl mx-auto">
        <FreeCard />
        <ProCard
          id="plan-basic"
          tier="Basic Pro"
          price="$4.99"
          cadence="/ month"
          tagline="Unlock 1-click Implement. Every ad channel."
          features={[
            "Everything in Free",
            "1-click Implement",
            "Adjust-Impact sliders",
            "Approve-all-safe · one tap",
            "Every ad platform · Google + Meta + TikTok",
            "Lifecycle email via Resend",
          ]}
          cta="Start with Basic Pro"
          plan="basic"
        />
        <ProCard
          id="plan-operator"
          tier="Operator Pro"
          price="$9.99"
          cadence="/ month"
          tagline="Fully hands-off. Autonomous mode + Copilot."
          features={[
            "Everything in Basic Pro",
            "Autonomous Mode toggle",
            "AI Profit Copilot chat",
            "Operator Modules · Image / SEO / Inventory",
            "Daily 7 AM Profit Briefing",
            "Competitive radar · 24/7",
            "Predictive inventory allocation",
          ]}
          cta="Activate Operator Pro"
          plan="operator"
          ribbon="Most loved"
          badge={`Recovers ${CASH_IMPACT_LABEL} in demo store`}
        />
      </div>

      {/* Money-back + trust strip */}
      <div className="mt-10 flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-sm text-slate-500">
        <span className="inline-flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-emerald-600" /> 30-day money-back on annual</span>
        <span className="inline-flex items-center gap-2"><Sparkles className="w-4 h-4 text-emerald-600" /> Annual · save 20%</span>
        <span className="inline-flex items-center gap-2"><ShoppingBag className="w-4 h-4 text-emerald-600" /> Free install · Shopify App Store</span>
      </div>

      {/* Feature comparison table */}
      <div
        data-testid="pricing-matrix"
        className="mt-12 md:mt-14 max-w-4xl mx-auto rounded-3xl border border-slate-200 bg-white overflow-hidden"
      >
        <div className="grid grid-cols-[1fr_auto_auto] items-center px-5 md:px-7 py-4 bg-slate-50 border-b border-slate-200 text-[10px] tracking-[0.18em] uppercase font-bold text-slate-500">
          <span>Feature</span>
          <span className="w-16 text-center">Free</span>
          <span className="w-16 text-center text-emerald-700">Pro</span>
        </div>
        {FEATURE_MATRIX.map((f, i) => (
          <div
            key={f.label}
            data-testid={`matrix-row-${i}`}
            onClick={f.free ? undefined : () => open(`matrix-${f.label.slice(0, 32)}`)}
            role={f.free ? undefined : "button"}
            tabIndex={f.free ? undefined : 0}
            onKeyDown={f.free ? undefined : (e) => { if (e.key === "Enter" || e.key === " ") open(`matrix-${f.label.slice(0, 32)}`); }}
            className={`grid grid-cols-[1fr_auto_auto] items-center px-5 md:px-7 py-3 border-b border-slate-100 last:border-b-0 text-sm ${
              f.free ? "" : "cursor-pointer hover:bg-emerald-50/40 transition"
            }`}
          >
            <span className="text-slate-800">{f.label}</span>
            <span className="w-16 text-center">
              {f.free
                ? <Check className="w-4 h-4 text-emerald-600 inline" />
                : <Lock className="w-4 h-4 text-slate-400 inline" />}
            </span>
            <span className="w-16 text-center">
              <Check className="w-4 h-4 text-emerald-600 inline" />
            </span>
          </div>
        ))}
        <div className="px-5 md:px-7 py-4 bg-slate-50 text-center text-xs text-slate-500">
          Free tier is <strong className="text-slate-900">forever</strong>. Upgrade the day one approved fix pays for it.
        </div>
      </div>
    </div>
    {modal}
  </section>
  );
};

export default Pricing;
